import { supabaseClient } from "@/lib/supabase";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import type { OwnOptionT } from "../ownOptions";
import { useOwnOptionsStore } from "../useOwnOptionsStore";

/** Own options by id, for a follow-on question that offers them (options_from with own: true). */
export async function selectOwnOptions(optionIds: readonly string[]): Promise<OwnOptionT[]> {
    const { data } = await supabaseClient
        .from("options")
        .select("option_id, input_id, label")
        .in("option_id", [...optionIds])
        .eq("is_private", true)
        .throwOnError();
    return data.flatMap((row) => (row.input_id ? [{ ...row, input_id: row.input_id }] : []));
}

export const ownOptionsQueryOptions = (optionIds: readonly string[]) =>
    queryOptions({
        queryKey: ["own-options", ...optionIds],
        queryFn: () => selectOwnOptions(optionIds),
        enabled: optionIds.length > 0,
    });

/**
 * The own options with these ids, in the same order: from this browser when known (just added, or an anonymous
 * link's, which exist only here until submit), otherwise fetched. Ids not found are left out.
 */
export function useOwnOptions(optionIds: readonly string[]): OwnOptionT[] {
    const stored = useOwnOptionsStore((state) => state.own_options);
    const missing = useMemo(
        () => optionIds.filter((id) => !stored.some((option) => option.option_id === id)),
        [optionIds, stored]
    );
    const { data: fetched } = useQuery(ownOptionsQueryOptions(missing));

    return useMemo(() => {
        const byId = new Map<string, OwnOptionT>();
        for (const option of fetched ?? []) byId.set(option.option_id, option);
        for (const option of stored) byId.set(option.option_id, option);
        return optionIds.flatMap((id) => {
            const option = byId.get(id);
            return option ? [{ option_id: option.option_id, input_id: option.input_id, label: option.label }] : [];
        });
    }, [optionIds, stored, fetched]);
}
